import React, { PureComponent } from 'react'
import * as cornerstone from 'cornerstone-core'
import ReferenceLinesOverlay from './ReferenceLinesOverlay'
import {
  buildScoutReferenceLines,
  readPlaneGeometry,
} from '../../utils/scoutReferenceLines'

/**
 * Panel flotante/colapsable con la serie Scout + overlay de líneas.
 * No altera el layout 2×2 MPR+Vol3D.
 */
export default class ScoutFloatingPanel extends PureComponent {
  elementRef = React.createRef()
  state = {
    collapsed: false,
    imageWidth: 512,
    imageHeight: 512,
    lines: [],
  }

  componentDidMount() {
    this.enableAndShow()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.scoutFile !== this.props.scoutFile) {
      this.enableAndShow()
      return
    }
    if (prevProps.volumeFiles !== this.props.volumeFiles) {
      this.rebuildLines()
    }
    // activeSliceIndex: solo re-render (PureComponent); no rebuildLines (parpadeo).
  }

  componentWillUnmount() {
    const el = this.elementRef.current
    if (el) {
      try {
        cornerstone.disable(el)
      } catch (e) {
        /* ignore */
      }
    }
  }

  enableAndShow = async () => {
    const el = this.elementRef.current
    const { scoutFile } = this.props
    if (!el || !scoutFile) return

    try {
      cornerstone.enable(el)
    } catch (e) {
      /* already enabled */
    }

    try {
      let image = scoutFile.image
      if (!image && scoutFile.imageId) {
        image = await cornerstone.loadImage(scoutFile.imageId)
      }
      if (!image) return
      cornerstone.displayImage(el, image)
      cornerstone.fitToWindow(el)
      const geo = readPlaneGeometry(scoutFile) || readPlaneGeometry({ image })
      this.setState(
        {
          imageWidth: (geo && geo.cols) || image.columns || 512,
          imageHeight: (geo && geo.rows) || image.rows || 512,
        },
        () => this.rebuildLines()
      )
    } catch (e) {
      console.warn('[ScoutFloatingPanel] display failed', e)
    }
  }

  rebuildLines = () => {
    const { scoutFile, volumeFiles } = this.props
    if (!scoutFile || !volumeFiles) {
      this.setState({ lines: [] })
      return
    }
    const lines = buildScoutReferenceLines(scoutFile, volumeFiles)
    this.setState({ lines })
  }

  handleLineClick = (sliceIndex) => {
    if (typeof this.props.onSliceSelect === 'function') {
      this.props.onSliceSelect(sliceIndex)
    }
  }

  toggleCollapsed = () => {
    this.setState((s) => ({ collapsed: !s.collapsed }))
  }

  render() {
    const {
      open,
      scoutSyncEnabled,
      activeSliceIndex = 0,
      onClose,
      title,
    } = this.props
    if (!open) return null

    const { collapsed, lines, imageWidth, imageHeight } = this.state
    const label =
      title ||
      (this.props.scoutFile &&
        this.props.scoutFile.series &&
        this.props.scoutFile.series.seriesDescription) ||
      'Scout'

    return (
      <div
        className="scout-floating-panel"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 72,
          width: collapsed ? 220 : 320,
          zIndex: 1400,
          background: 'rgba(18, 22, 28, 0.94)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 8,
          boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
          color: '#e8eef5',
          overflow: 'hidden',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            cursor: 'default',
          }}
        >
          <strong style={{ fontSize: 13 }}>{label}</strong>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={this.toggleCollapsed}
              style={btnStyle}
              title={collapsed ? 'Expandir' : 'Colapsar'}
            >
              {collapsed ? '▢' : '—'}
            </button>
            {onClose ? (
              <button type="button" onClick={onClose} style={btnStyle} title="Cerrar">
                ✕
              </button>
            ) : null}
          </div>
        </div>
        {!collapsed && (
          <div style={{ position: 'relative', width: '100%', height: 240, background: '#000' }}>
            <div
              ref={this.elementRef}
              className="cornerstone-enabled-image"
              style={{ width: '100%', height: '100%' }}
            />
            <ReferenceLinesOverlay
              visible={!!scoutSyncEnabled}
              lines={lines}
              activeIndex={activeSliceIndex}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              onLineClick={this.handleLineClick}
            />
          </div>
        )}
        {!collapsed && (
          <div style={{ padding: '6px 10px', fontSize: 11, opacity: 0.8 }}>
            Clic en una línea para saltar al corte. Scroll axial actualiza la línea activa.
          </div>
        )}
      </div>
    )
  }
}

const btnStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.25)',
  color: '#e8eef5',
  borderRadius: 4,
  width: 26,
  height: 24,
  cursor: 'pointer',
  lineHeight: '20px',
  padding: 0,
}
